import { GridRowId } from '@mui/x-data-grid';
import { filter, includes, map, toString } from 'es-toolkit/compat';
import React from 'react';
import { DelimitedArrayParam, StringParam, useQueryParam } from 'use-query-params';
import { useRangeSelection } from 'utils/useRangeSelection';

export type SelectionMode = 'select' | 'omit';
export interface RowSelectionContextProps {
  allSelected: (numRows: number) => boolean;
  noneSelected: (numRows: number) => boolean;
  someSelected: (numRows: number) => boolean;
  selectedRows: GridRowId[];
  omittedRows: GridRowId[];
  toggleSelection: (row: GridRowId, selectRange?: boolean) => void;
  setRangeSelectionOrder: (rowIds: GridRowId[]) => void;
  previewSelection: (row: GridRowId, selectRange?: boolean) => void;
  clearSelectionPreview: () => void;
  isRowDisplayedSelected: (row: GridRowId) => boolean;
  isRowSelectionPreviewed: (row: GridRowId) => boolean;
  selectionMode: SelectionMode;
  setSelectionMode: (mode: SelectionMode) => void;
  clearSelection: () => void;
  selectAll: () => void;
  isRowSelected: (row: GridRowId) => boolean;
  selectedRowsCount: (numRows: number) => number;
}

interface RowSelectionContextProviderProps {
  children?: React.ReactNode;
  urlParamPrefix?: string;
  convertIdFromString?: (id: string) => GridRowId;
}

const RowSelectionContext = React.createContext<RowSelectionContextProps | undefined>(undefined);

const toggleRowIdSelection = (
  rowId: GridRowId,
  rowsBeforeConversion: any[],
  convertIdFromString?: (id: string) => GridRowId
) => {
  const rows: GridRowId[] = convertIdFromString
    ? map(rowsBeforeConversion, (id) => convertIdFromString(id))
    : rowsBeforeConversion;
  return includes(rows, rowId) ? filter(rows, (r) => r !== rowId) : [...rows, rowId];
};

const defaultSelectionMode: SelectionMode = 'select';

export const RowSelectionContextProvider: React.FunctionComponent<
  React.PropsWithChildren<RowSelectionContextProviderProps>
> = ({ children, urlParamPrefix, convertIdFromString }: RowSelectionContextProviderProps) => {
  const [selectionModeState, setSelectionModeState] = React.useState<SelectionMode>(defaultSelectionMode);
  const [selectedRowsState, setSelectedRowsState] = React.useState<GridRowId[]>([]);
  const [omittedRowsState, setOmittedRowsState] = React.useState<GridRowId[]>([]);

  const [selectionModeUrl, setSelectionModeUrl] = useQueryParam(`${urlParamPrefix}SelectionMode`, StringParam);
  const [selectedRowsUrl, setSelectedRowsUrl] = useQueryParam(`${urlParamPrefix}SelectedRows`, DelimitedArrayParam);
  const [omittedRowsUrl, setOmittedRowsUrl] = useQueryParam(`${urlParamPrefix}OmittedRows`, DelimitedArrayParam);

  const { selectionMode, setSelectionMode, selectedRows, setSelectedRows, omittedRows, setOmittedRows } =
    React.useMemo((): {
      selectionMode: SelectionMode;
      setSelectionMode: (mode: SelectionMode) => void;
      selectedRows: GridRowId[];
      setSelectedRows: React.Dispatch<React.SetStateAction<GridRowId[]>>;
      omittedRows: GridRowId[];
      setOmittedRows: React.Dispatch<React.SetStateAction<GridRowId[]>>;
    } => {
      const isUrlSelectionModeValid = includes(['select', 'omit'], selectionModeUrl);
      const innerSelectionMode: SelectionMode = urlParamPrefix
        ? isUrlSelectionModeValid
          ? (selectionModeUrl as SelectionMode)
          : defaultSelectionMode
        : selectionModeState;

      const innerSetSelectionMode = urlParamPrefix
        ? (mode: SelectionMode) => setSelectionModeUrl(mode, 'replaceIn')
        : setSelectionModeState;

      const innerSelectedRows = urlParamPrefix
        ? map(selectedRowsUrl, (id) => convertIdFromString?.(id) ?? id)
        : selectedRowsState;
      const innerSetSelectedRows: React.Dispatch<React.SetStateAction<GridRowId[]>> = urlParamPrefix
        ? (param: React.SetStateAction<GridRowId[]>) => {
            const newIds = map(typeof param === 'function' ? param(innerSelectedRows) : param, toString);
            setSelectedRowsUrl(newIds, 'replaceIn');
          }
        : setSelectedRowsState;

      const innerOmittedRows = urlParamPrefix
        ? map(omittedRowsUrl, (id) => convertIdFromString?.(id) ?? id)
        : omittedRowsState;

      const innerSetOmittedRows: React.Dispatch<React.SetStateAction<GridRowId[]>> = urlParamPrefix
        ? (param: React.SetStateAction<GridRowId[]>) => {
            const newIds = map(typeof param === 'function' ? param(omittedRows) : param, toString);
            setOmittedRowsUrl(newIds, 'replaceIn');
          }
        : setOmittedRowsState;

      return {
        selectionMode: innerSelectionMode,
        setSelectionMode: innerSetSelectionMode,
        selectedRows: innerSelectedRows,
        setSelectedRows: innerSetSelectedRows,
        omittedRows: innerOmittedRows,
        setOmittedRows: innerSetOmittedRows,
      };
    }, [
      urlParamPrefix,
      selectionModeUrl,
      setSelectionModeUrl,
      selectedRowsUrl,
      setSelectedRowsUrl,
      omittedRowsUrl,
      setOmittedRowsUrl,
      selectionModeState,
      selectedRowsState,
      omittedRowsState,
      convertIdFromString,
    ]);

  const [rangeOrder, setRangeOrder] = React.useState<GridRowId[]>([]);
  const visualSelectedItems =
    selectionMode === 'omit' ? filter(rangeOrder, (id) => !includes(omittedRows, id)) : selectedRows;
  const setVisualSelectedItems: React.Dispatch<React.SetStateAction<GridRowId[]>> = (update) => {
    const next = typeof update === 'function' ? update(visualSelectedItems) : update;
    if (selectionMode === 'omit') {
      const omittedInOrder = filter(rangeOrder, (id) => !includes(next, id));
      const omittedOutsideOrder = filter(omittedRows, (id) => !includes(rangeOrder, id));
      setOmittedRows([...omittedOutsideOrder, ...omittedInOrder]);
      return;
    }
    setSelectedRows(next);
  };
  const {
    clearSelectionPreview,
    previewedSelectedItems,
    previewSelection: previewRangeSelection,
    resetRangeSelection,
    toggleSelection: toggleRangeSelection,
  } = useRangeSelection({
    orderedItems: rangeOrder,
    selectedItems: visualSelectedItems,
    setSelectedItems: setVisualSelectedItems,
  });
  const resetRangeSelectionRef = React.useRef(resetRangeSelection);
  resetRangeSelectionRef.current = resetRangeSelection;
  const setRangeSelectionOrder = React.useCallback((rowIds: GridRowId[]) => {
    setRangeOrder((current) => {
      if (current.length === rowIds.length && current.every((id, index) => id === rowIds[index])) return current;
      return rowIds;
    });
  }, []);

  React.useEffect(() => {
    resetRangeSelectionRef.current();
  }, [rangeOrder]);

  const contextValue: RowSelectionContextProps = React.useMemo(() => {
    const rowIsSelected = (row: GridRowId) =>
      selectionMode === 'select' ? includes(selectedRows, row) : !includes(omittedRows, row);

    return {
      allSelected: (numRows: number) =>
        numRows > 0 && (selectionMode === 'select' ? selectedRows.length === numRows : omittedRows.length === 0),
      noneSelected: (numRows: number) =>
        numRows > 0 && (selectionMode === 'select' ? selectedRows.length === 0 : omittedRows.length === numRows),
      someSelected: (numRows: number) =>
        numRows > 0 && (selectionMode === 'select' ? selectedRows.length > 0 : omittedRows.length < numRows),
      toggleSelection: (row: GridRowId, selectRange = false) => {
        if (rangeOrder.length) {
          toggleRangeSelection(row, selectRange);
          return;
        }
        if (selectionMode === 'select') {
          setSelectedRows((prev) => toggleRowIdSelection(row, prev, convertIdFromString));
        } else {
          setOmittedRows((prev) => toggleRowIdSelection(row, prev, convertIdFromString));
        }
      },
      setRangeSelectionOrder,
      previewSelection: (row: GridRowId, selectRange = false) => {
        if (!includes(rangeOrder, row)) return;
        previewRangeSelection(row, selectRange);
      },
      clearSelectionPreview,
      isRowDisplayedSelected: (row: GridRowId) =>
        previewedSelectedItems ? includes(previewedSelectedItems, row) : rowIsSelected(row),
      isRowSelectionPreviewed: (row: GridRowId) =>
        previewedSelectedItems !== undefined && includes(previewedSelectedItems, row) !== rowIsSelected(row),
      selectedRows: selectedRows.sort(),
      omittedRows: omittedRows.sort(),
      selectedRowsCount: (numRows: number) =>
        selectionMode === 'select' ? selectedRows.length : numRows - omittedRows.length,
      selectionMode,
      setSelectionMode,
      selectAll: () => {
        resetRangeSelectionRef.current();
        setSelectionMode('omit');
        setOmittedRows([]);
      },
      clearSelection: () => {
        resetRangeSelectionRef.current();
        setSelectionMode('select');
        setSelectedRows([]);
      },
      isRowSelected: rowIsSelected,
    };
  }, [
    selectedRows,
    setSelectedRows,
    omittedRows,
    setOmittedRows,
    selectionMode,
    setSelectionMode,
    rangeOrder,
    toggleRangeSelection,
    setRangeSelectionOrder,
    convertIdFromString,
    previewedSelectedItems,
    previewRangeSelection,
    clearSelectionPreview,
  ]);

  return <RowSelectionContext.Provider value={contextValue}>{children}</RowSelectionContext.Provider>;
};

export const useRowSelectionContextOptional = () =>
  React.useContext(RowSelectionContext as React.Context<RowSelectionContextProps>);

export const useRowSelectionContext = () => {
  const context = useRowSelectionContextOptional();
  if (context === undefined) {
    throw new Error('useRowSelectionContext must be used within a RowSelectionContextProvider');
  }
  return context;
};
